/**
 * AI generation history — localStorage, scoped by customer + product.
 */
(function () {
  "use strict";

  const STORAGE_PREFIX = "tinypresent_ai_gen_history";
  const MAX_ENTRIES_PER_PRODUCT = 40;

  function getCustomerId() {
    const key =
      window.AI_UPLOAD_CONFIG?.customerIdStorageKey || "tinypresent_customer_id";
    let id = localStorage.getItem(key);
    if (!id) {
      id =
        "guest-" +
        (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString(36) + Math.random().toString(36).slice(2));
      localStorage.setItem(key, id);
    }
    return id;
  }

  function storageKey(productId) {
    return `${STORAGE_PREFIX}_${getCustomerId()}_${productId}`;
  }

  function readEntries(productId) {
    if (!productId) return [];
    try {
      const raw = localStorage.getItem(storageKey(productId));
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function writeEntries(productId, entries) {
    if (!productId) return;
    try {
      localStorage.setItem(storageKey(productId), JSON.stringify(entries));
    } catch (e) {
      console.warn("[AiGenerationHistory] localStorage write failed", e);
    }
  }

  function getProductIdFromSection(root) {
    if (typeof window.getProductGenerationConfig === "function") {
      const config = window.getProductGenerationConfig(root);
      if (config.productId != null && config.productId !== "") {
        return String(config.productId);
      }
    }

    const section =
      root instanceof Element && root.matches(".ai-upload-section")
        ? root
        : root?.querySelector?.(".ai-upload-section");
    const id = section?.dataset?.productId;
    return id ? String(id) : null;
  }

  /**
   * Normalize legacy and current history entry shapes.
   *
   * @param {Record<string, unknown>|null|undefined} entry
   * @returns {Record<string, unknown>|null}
   */
  function normalizeEntry(entry) {
    if (!entry || typeof entry !== "object") return null;

    const generatedImageUrl = String(
      entry.generatedImageUrl || entry.url || ""
    ).trim();
    if (!generatedImageUrl) return null;

    const legacyUrls = Array.isArray(entry.userImageUrls)
      ? entry.userImageUrls.filter(Boolean)
      : [];
    const userImageUrl = String(
      entry.userImageUrl || legacyUrls[0] || ""
    ).trim();
    const userImageUrl2 = String(
      entry.userImageUrl2 || legacyUrls[1] || ""
    ).trim();
    const userImageUrls = [userImageUrl, userImageUrl2].filter(Boolean);

    const customProperties =
      entry.customProperties && typeof entry.customProperties === "object"
        ? entry.customProperties
        : {};

    return {
      url: generatedImageUrl,
      generatedImageUrl,
      productId: String(entry.productId || ""),
      timestamp: Number(entry.timestamp) || Date.now(),
      taskId: String(entry.taskId || ""),
      generationMode: String(entry.generationMode || "photo"),
      variantId: entry.variantId ? String(entry.variantId) : "",
      variantTitle: String(entry.variantTitle || ""),
      variantOptions: Array.isArray(entry.variantOptions)
        ? entry.variantOptions
        : [],
      customProperties,
      userImageUrl,
      userImageUrl2,
      userImageUrls,
    };
  }

  window.AiGenerationHistory = {
    getCustomerId,
    getProductIdFromSection,
    normalizeEntry,

    /**
     * @param {string} url Generated image URL
     * @param {string|number} productId
     * @param {{
     *   taskId?: string,
     *   generationMode?: string,
     *   variantId?: string,
     *   variantTitle?: string,
     *   variantOptions?: { name: string, value: string }[],
     *   customProperties?: Record<string, string>,
     *   userImageUrl?: string,
     *   userImageUrl2?: string,
     *   userImageUrls?: string[],
     * } | null} [context]
     */
    add(url, productId, context) {
      if (!url || !productId) return;

      const generatedImageUrl = String(url).trim();
      const legacyUrls = Array.isArray(context?.userImageUrls)
        ? context.userImageUrls.filter(Boolean)
        : [];
      const userImageUrl = String(
        context?.userImageUrl || legacyUrls[0] || ""
      ).trim();
      const userImageUrl2 = String(
        context?.userImageUrl2 || legacyUrls[1] || ""
      ).trim();
      const userImageUrls = [userImageUrl, userImageUrl2].filter(Boolean);

      const entries = readEntries(productId)
        .map((entry) => normalizeEntry(entry))
        .filter(Boolean);
      const filtered = entries.filter((e) => e.generatedImageUrl !== generatedImageUrl);

      filtered.unshift({
        url: generatedImageUrl,
        generatedImageUrl,
        productId: String(productId),
        timestamp: Date.now(),
        taskId: context?.taskId ? String(context.taskId) : "",
        generationMode: context?.generationMode
          ? String(context.generationMode)
          : "photo",
        variantId: context?.variantId ? String(context.variantId) : "",
        variantTitle: context?.variantTitle || "",
        variantOptions: Array.isArray(context?.variantOptions)
          ? context.variantOptions
          : [],
        customProperties:
          context?.customProperties &&
          typeof context.customProperties === "object"
            ? context.customProperties
            : {},
        userImageUrl,
        userImageUrl2,
        userImageUrls,
      });

      writeEntries(productId, filtered.slice(0, MAX_ENTRIES_PER_PRODUCT));
    },

    getForProduct(productId) {
      return readEntries(productId)
        .map((entry) => normalizeEntry(entry))
        .filter(Boolean);
    },

    remove(url, productId) {
      if (!url || !productId) return;
      const target = String(url).trim();
      const entries = readEntries(productId)
        .map((entry) => normalizeEntry(entry))
        .filter((entry) => entry && entry.generatedImageUrl !== target);
      writeEntries(productId, entries);
    },
  };
})();
